import { Page } from "components/Page"
import { Wrapper } from "components/Wrapper"
import { PlanCard } from "domains/plan/PlanCard"
import { Text } from "components/Text"
import { useAsync } from "@react-hook/async"
import { Alerter } from "components/Alert"
import { useRouter } from "components/Router"
import { useTranslator } from "components/Translator"
import * as React from "react"
import { Button } from "components/Button"
import { Modal, ModalContent, ModalActions } from "components/Modal"
import { GiftCard } from "domains/gift/GiftCard"
import { Pressable } from "components/Pressable"
import cx from "classnames"

export function ClubPlanShowPage({ plan, godfather }) {
  const router = useRouter()
  const translator = useTranslator()

  const [showGifts, setShowGifts] = React.useState(false)
  const [selectedGift, setSelectedGift] = React.useState(null)

  const [addPlanRequest, addPlanToCart] = useAsync(async () => {
    try {
      const url = router.generate("app_user_order_cart_plan_add", {
        planId: plan.id,
        planPriceId: plan.prices[0].id,
      })
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      const responseData = await response.json()

      if (response.ok) {
        if (plan.gifts.length === 0) {
          if (responseData.message) {
            Alerter.success(responseData.message)
          }

          goToCart()
        } else {
          setShowGifts(true)
        }
      } else {
        if (responseData.message) {
          Alerter.error(responseData.message)
        }
      }
    } catch (err) {
      console.log(err)
      Alerter.error(
        translator.trans("club.plan.show.addToCartError", null, "pages"),
      )
    }
  })

  const [addGiftRequest, addGiftToCart] = useAsync(async (gift) => {
    try {
      const url = router.generate("app_user_order_cart_gift_add", {
        giftId: gift.id,
      })
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      const responseData = await response.json()

      if (response.ok) {
        if (responseData.message) {
          Alerter.success(responseData.message)
        }

        setShowGifts(false)
        goToCart()
      } else {
        if (responseData.message) {
          Alerter.error(responseData.message)
        }
      }
    } catch (err) {
      console.log(err)
      Alerter.error(
        translator.trans("club.plan.index.addToCartError", null, "pages"),
      )
    }
  })

  const goToCart = () => {
    router.redirect({ name: "app_user_order_cart_index" })
  }

  return (
    <Page padded>
      <Wrapper size="small">
        <div className="space-y-16">
          <PlanCard
            plan={plan}
            color="secondary"
            godfather={godfather}
            onSelect={addPlanToCart}
            busy={addPlanRequest.status === "loading"}
            disabled={["loading"].includes(addPlanRequest.status)}
            requireLogin={true}
          />
        </div>
      </Wrapper>
      <Modal
        isOpen={showGifts}
        onRequestClose={() => setShowGifts(false)}
        title={translator.trans(
          "club.plan.show.giftsModal.title",
          null,
          "pages",
        )}
      >
        <ModalContent>
          <div className="space-y-8">
            <Text tag="p">
              {translator.trans("club.plan.show.giftsModal.description")}
            </Text>
            <ul className="grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-2 lg:gap-4 2xl:grid-cols-4">
              {plan.gifts.map((gift) => {
                return (
                  <li key={gift.id}>
                    <Pressable
                      onClick={() => setSelectedGift(gift)}
                      className={cx(
                        "rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-light",
                        {
                          "ring-2 ring-primary-default":
                            selectedGift?.id === gift.id,
                        },
                      )}
                    >
                      <GiftCard gift={gift} />
                    </Pressable>
                  </li>
                )
              })}
            </ul>
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            color="secondary"
            busy={addGiftRequest.status === "loading"}
            disabled={!selectedGift || addGiftRequest.status === "loading"}
            onClick={() => addGiftToCart(selectedGift)}
          >
            {translator.trans("club.plan.show.giftsModal.select")}
          </Button>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setShowGifts(false)}
            disabled={addGiftRequest.status === "loading"}
          >
            {translator.trans(
              "club.plan.show.giftsModal.cancel",
              null,
              "pages",
            )}
          </Button>
        </ModalActions>
      </Modal>
    </Page>
  )
}
